// src/i18n/LanguageProvider.tsx

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  createI18nAsync,
  type AppLang,
  type I18nConstants,
} from './locales/i18n.factory';

type ContextType = {
  lang: AppLang;
  setLang: (l: AppLang) => void;
  toggleLang: () => void;   // ⭐️ חדש
  i18n: I18nConstants;
};

const I18nContext = createContext<ContextType | null>(null);
const LANG_STORAGE_KEY = 'djeliormor.lang';
const SUPPORTED_LANGS: AppLang[] = ['he', 'en', 'ru', 'fr'];

function getInitialLang(): AppLang {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (SUPPORTED_LANGS.includes(stored as AppLang)) {
      return stored as AppLang;
    }
  } catch {}

  return 'he';
}

export const LanguageProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [lang, setLang] = useState<AppLang>(() => getInitialLang());
  const [i18n, setI18n] = useState<I18nConstants | null>(null);

  const toggleLang = () =>
    setLang((prev) => {
      if (prev === 'he') return 'en';
      if (prev === 'en') return 'ru';
      if (prev === 'ru') return 'fr';
      return 'he';
    });
  useEffect(() => {
    let cancelled = false;

    setI18n(null);

    createI18nAsync(lang).then((nextI18n) => {
      if (!cancelled) setI18n(nextI18n);
    });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  // ⭐️⭐️⭐️ שינוי כיוון האתר כולו
  useEffect(() => {
    const isRTL = lang === 'he';

    const html = document.documentElement;

    html.lang = lang;
    html.dir = isRTL ? 'rtl' : 'ltr';

    // optional — class helpers
    html.classList.toggle('rtl', isRTL);
    html.classList.toggle('ltr', !isRTL);

    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {}

  }, [lang]);

  const value = useMemo(
    () => (i18n ? { lang, setLang, toggleLang, i18n } : null),
    [lang, i18n]
  );

  if (!value) return null;

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): ContextType => {
  const ctx = useContext(I18nContext);

  if (!ctx) {
    throw new Error(
      'useI18n must be used inside LanguageProvider'
    );
  }

  return ctx;
};
